import { formatCompliancePdfDate } from '@/features/compliance-studio/machinery-directive/utils/buildCompliancePdfCoverMarkdown'

function escapeHtmlForPdfCell(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function metaTableRow(label: string, valueInnerHtml: string): string {
  return `<tr><td class="pdf-cover-meta-label">${escapeHtmlForPdfCell(label)}</td><td class="pdf-cover-meta-value">${valueInnerHtml}</td></tr>`
}

function displayOrNotAvailable(
  value: string | undefined,
  notAvailable: string
): string {
  const trimmed = value?.trim() ?? ''
  if (trimmed !== '') {
    return trimmed
  }
  return notAvailable
}

export type PackagingMinimisationPdfCoverLabels = {
  reportReferenceNo: string
  dateOfAssessment: string
  packagingName: string
  packagingDescription: string
  packagingIdentificationNumber: string
  intendedUse: string
  photoOfPackaging: string
  company: string
  companyAddress: string
  standardsApplied: string
  testProcedure: string
  numberOfPages: string
  notAvailable: string
  preparedBy: string
  approvedBy: string
}

export type BuildPackagingMinimisationPdfCoverMarkdownInput = {
  documentTitle: string
  subtitle: string
  description: string
  labels: PackagingMinimisationPdfCoverLabels
  // Data fields
  reportReferenceNo?: string
  dateOfAssessment?: string
  packagingName?: string
  packagingDescription?: string
  packagingIdentificationNumber?: string
  intendedUse?: string
  photoOfPackaging?: string // Markdown image syntax
  companyName?: string
  companyAddress?: string
  standardsApplied?: string
  testProcedure?: string
  numberOfPages?: string
  formatDate: (iso: string) => string
  omitPhotoRow?: boolean
  omitNumberOfPagesRow?: boolean
}

/**
 * First page of exported Packaging Minimisation Assessment PDFs.
 * Includes title, subtitle, description, metadata table, and signature lines.
 * Metadata is an HTML table (no header row), centered via pdf-cover CSS.
 */
export function buildPackagingMinimisationPdfCoverMarkdown(
  input: BuildPackagingMinimisationPdfCoverMarkdownInput
): string {
  const notAvailable = input.labels.notAvailable

  const reportReferenceDisplay = displayOrNotAvailable(
    input.reportReferenceNo,
    notAvailable
  )
  const packagingNameDisplay = displayOrNotAvailable(
    input.packagingName,
    notAvailable
  )
  const packagingDescriptionDisplay = displayOrNotAvailable(
    input.packagingDescription,
    notAvailable
  )
  const packagingIdentificationNumberDisplay = displayOrNotAvailable(
    input.packagingIdentificationNumber,
    notAvailable
  )
  const intendedUseDisplay = displayOrNotAvailable(
    input.intendedUse,
    notAvailable
  )
  const photoOfPackagingDisplay = displayOrNotAvailable(
    input.photoOfPackaging,
    notAvailable
  )
  const standardsAppliedDisplay = displayOrNotAvailable(
    input.standardsApplied,
    notAvailable
  )
  const testProcedureDisplay = displayOrNotAvailable(
    input.testProcedure,
    notAvailable
  )
  const numberOfPagesDisplay = displayOrNotAvailable(
    input.numberOfPages,
    notAvailable
  )

  const dateFormatted = input.dateOfAssessment != null
    ? input.formatDate(input.dateOfAssessment)
    : notAvailable

  const companyNameDisplay = displayOrNotAvailable(
    input.companyName,
    notAvailable
  )
  const companyAddressDisplay = displayOrNotAvailable(
    input.companyAddress,
    notAvailable
  )

  const tableRows: string[] = [
    metaTableRow(input.labels.reportReferenceNo, escapeHtmlForPdfCell(reportReferenceDisplay)),
    metaTableRow(input.labels.dateOfAssessment, escapeHtmlForPdfCell(dateFormatted)),
    metaTableRow(input.labels.packagingName, escapeHtmlForPdfCell(packagingNameDisplay)),
    metaTableRow(input.labels.packagingDescription, escapeHtmlForPdfCell(packagingDescriptionDisplay)),
    metaTableRow(input.labels.packagingIdentificationNumber, escapeHtmlForPdfCell(packagingIdentificationNumberDisplay)),
    metaTableRow(input.labels.intendedUse, escapeHtmlForPdfCell(intendedUseDisplay)),
  ]

  if (input.omitPhotoRow !== true) {
    tableRows.push(
      metaTableRow(
        input.labels.photoOfPackaging,
        escapeHtmlForPdfCell(photoOfPackagingDisplay)
      )
    )
  }

  tableRows.push(
    metaTableRow(input.labels.company, escapeHtmlForPdfCell(companyNameDisplay)),
    metaTableRow(
      input.labels.companyAddress,
      escapeHtmlForPdfCell(companyAddressDisplay)
    ),
    metaTableRow(
      input.labels.standardsApplied,
      escapeHtmlForPdfCell(standardsAppliedDisplay)
    ),
    metaTableRow(
      input.labels.testProcedure,
      escapeHtmlForPdfCell(testProcedureDisplay)
    )
  )

  if (input.omitNumberOfPagesRow !== true) {
    tableRows.push(
      metaTableRow(
        input.labels.numberOfPages,
        escapeHtmlForPdfCell(numberOfPagesDisplay)
      )
    )
  }

  tableRows.push(
    metaTableRow(input.labels.preparedBy, escapeHtmlForPdfCell('')),
    metaTableRow(input.labels.approvedBy, escapeHtmlForPdfCell(''))
  )

  const rows = tableRows.join('\n')

  const table = `<div class="pdf-cover-meta-table-wrap">
<table class="pdf-cover-meta-table">
<tbody>
${rows}
</tbody>
</table>
</div>`

  return [
    `# ${input.documentTitle}`,
    '',
    `## ${input.subtitle}`,
    '',
    input.description,
    '',
    table,
  ].join('\n')
}

export { formatCompliancePdfDate }
